import styled from '@emotion/styled';
import { Artist } from '../data/types';
import { ArtworkCard } from './ArtworkCard';

const Section = styled.div<{ variant?: 'default' | 'paper' }>`
  /* Mobile-first */
  margin: 1.5rem 0;
  padding: 1rem 0.75rem;
  border-radius: 8px;
  background: ${props => props.variant === 'paper' 
    ? 'rgba(247, 243, 235, 0.95)' 
    : 'rgba(0, 0, 0, 0.4)'};
  backdrop-filter: blur(10px);
  border: ${props => props.variant === 'paper' 
    ? '1px solid rgba(109, 107, 73, 0.15)' 
    : '1px solid rgba(255, 255, 255, 0.1)'};
  box-shadow: ${props => props.variant === 'paper' 
    ? '0 4px 20px rgba(109, 107, 73, 0.12)' 
    : 'none'};
  scroll-margin-top: max(4rem, env(safe-area-inset-top));

  @media (min-width: 640px) {
    margin: 3rem 0;
    padding: 1.5rem 1rem;
  }

  @media (min-width: 768px) {
    margin: 6rem 0;
    padding: 3rem;
    scroll-margin-top: 8rem;
    box-shadow: ${props => props.variant === 'paper' 
      ? '0 8px 32px rgba(109, 107, 73, 0.15)' 
      : 'none'};
  }
`;

const Name = styled.h2<{ variant?: 'default' | 'paper' }>`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: ${props => props.variant === 'paper' ? '#2c2a26' : '#fff'};
  text-transform: uppercase;
  font-family: ${props => props.variant === 'paper' ? 'Georgia, "Times New Roman", serif' : 'inherit'};

  @media (min-width: 640px) {
    font-size: 1.25rem;
  }

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ThaiName = styled.span<{ variant?: 'default' | 'paper' }>`
  display: block;
  font-size: 0.8rem;
  color: ${props => props.variant === 'paper' ? '#6D6B49' : 'rgba(255, 255, 255, 0.7)'};
  margin-bottom: 1rem;
  font-weight: 300;

  @media (min-width: 640px) {
    font-size: 0.85rem;
    margin-bottom: 1.5rem;
  }

  @media (min-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2.5rem;
  }
`;

const Background = styled.div<{ variant?: 'default' | 'paper' }>`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: ${props => props.variant === 'paper' 
    ? 'rgba(255, 255, 255, 0.5)' 
    : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 8px;

  @media (min-width: 768px) {
    margin-bottom: 3.5rem;
    padding: 2rem;
  }
`;

const BackgroundTitle = styled.h3<{ variant?: 'default' | 'paper' }>`
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
  font-weight: 500;
  color: ${props => props.variant === 'paper' ? '#2c2a26' : '#fff'};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: ${props => props.variant === 'paper' ? 1 : 0.9};

  @media (min-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 1.25rem;
  }
`;

const Concept = styled.div<{ variant?: 'default' | 'paper' }>`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: ${props => props.variant === 'paper' 
    ? 'rgba(255, 255, 255, 0.5)' 
    : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 8px;

  @media (min-width: 768px) {
    margin-bottom: 3.5rem;
    padding: 2rem;
  }
`;

const ContentText = styled.p<{ variant?: 'default' | 'paper' }>`
  color: ${props => props.variant === 'paper' ? '#4a4540' : 'rgba(255, 255, 255, 0.8)'};
  line-height: 1.6;
  margin-bottom: 1rem;
  font-size: 0.9rem;

  &:last-child {
    margin-bottom: 0;
  }

  @media (min-width: 768px) {
    font-size: 1rem;
    line-height: 1.8;
  }
`;

const Artworks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1.5rem;

  @media (min-width: 640px) {
    gap: 2.5rem;
    margin-top: 2rem;
  }

  @media (min-width: 768px) {
    gap: 6rem;
    margin-top: 4rem;
  }
`;

interface ArtistSectionProps {
  artist: Artist;
  variant?: 'default' | 'paper';
}

export const ArtistSection = ({ artist, variant = 'default' }: ArtistSectionProps) => {
  const hasBackground = (artist.background.en && artist.background.en.trim() !== '') ||
    (artist.background.th && artist.background.th.trim() !== '');
  const hasConcept = (artist.concept.description && artist.concept.description.trim() !== '') ||
    (artist.concept.descriptionThai && artist.concept.descriptionThai.trim() !== '');

  return (
    <Section id={artist.id} variant={variant}>
      <Name variant={variant}>{artist.name} {artist.nameThai && <ThaiName variant={variant}>{artist.nameThai}</ThaiName>}</Name>
      {hasBackground && (
        <Background variant={variant}>
          <BackgroundTitle variant={variant}>Background</BackgroundTitle>
          {artist.background.en && <ContentText variant={variant}>{artist.background.en}</ContentText>}
          {artist.background.th && <ContentText variant={variant}>{artist.background.th}</ContentText>}
        </Background>
      )}
      {hasConcept && (
        <Concept variant={variant}>
          <BackgroundTitle variant={variant}>
            {artist.concept.title}
            {artist.concept.titleThai && artist.concept.titleThai.trim() !== '' && ` (${artist.concept.titleThai})`}
          </BackgroundTitle>
          {artist.concept.description && <ContentText variant={variant}>{artist.concept.description}</ContentText>}
          {artist.concept.descriptionThai && <ContentText variant={variant}>{artist.concept.descriptionThai}</ContentText>}
        </Concept>
      )}
      <Artworks>
        {artist.artworks.map(artwork => (
          <ArtworkCard key={artwork.id} artwork={artwork} variant={variant} />
        ))}
      </Artworks>
    </Section>
  );
}; 